


import { Component, Vue } from "vue-property-decorator";

import BooleanField from "Web/Components/Common/Input/Boolean.vue";
import PopupWindow from "Web/Components/Popup/Window.vue";
import SimpleSelect from "Web/Components/V2/Select/Simple.vue";
import TablePagination from "Web/Components/Table/Pagination.vue";
import Tab from "Web/Components/Tabs/Tab.vue";
import Tabs from "Web/Components/Tabs/Index.vue";

import { AdsPublications, Customers } from "App";
import { $t, EventBus } from "App/IOC";
import { Activated, GroupOverview, GroupOverviewUser, Identifiable, Named } from "App/Contracts";
import { CustomNotification, PagerSorter } from "App/Models";
import { CustomValue } from "@Internal/Models";
import { Option } from "Web/Contracts";

interface GatewayOption {
    Id: number;
    Name: string;
    /* gateway_type id: 1 = FTP (code + alias + auto), 2 = API e.g. Amanda (identifier +
       password). */
    Type: number;
}

/* gateway_type id for API gateways (Amanda, Amepi, Vitrinellis api). */
const GATEWAY_TYPE_API = 2;

interface UserOption {
    Id: number;
    Label: string;
}

/* Associated portals of a single group (reached from the sale point settings list). The per-user
   setups come from /console/setup-gateway/list scoped by the groupId carried in the route and are
   grouped here by portal. "Créer un portail" first opens a modal listing the available portals
   (general config); picking one opens a second modal to configure it for a group user (sent code /
   alias / auto broadcast). */
@Component( {
    components: { BooleanField, PopupWindow, SimpleSelect, Tab, TablePagination, Tabs }
} )
export default class AdsPublicationSalePointPortalsView extends Vue {
    /* Flat list of the group's per-user portal setups, one row per setup (matches the desktop
       "Portails d'annonces" list: logo / nom / code envoyé / nb annonces / diffusion / dernier
       envoi / créé le). */
    private setups: any[] = [];
    private loading: boolean = true;

    /* Full group overview (group + linked customer + owner + members), fetched by groupId. */
    private overview: GroupOverview | null = null;

    /* Portals and users each in their own tab, paged on the front ( Fred, 02/10/2026 ): a
       network carries dozens of both, all already loaded. */
    private readonly pageLimits: number[] = [ 10, 25, 50 ];
    private membersPage: number = 1;
    private membersLimit: number = 10;
    private setupsPage: number = 1;
    private setupsLimit: number = 10;

    /* Two-step creation: pick a gateway, then configure it. */
    private pickingPortal: boolean = false;
    private configuring: boolean = false;

    /* Edit mode reuses the configuration modal; 0 = creating a new setup. */
    private editingId: number = 0;

    private gateways: GatewayOption[] = [];
    private groupUsers: UserOption[] = [];

    private selectedGatewayId: number = 0;
    private selectedGatewayName: string = "";
    private selectedGatewayType: number = 1;
    private selectedUserId: number = 0;
    private formCode: string = "";
    private formAlias: string = "";
    private formAuto: boolean = false;
    /* API gateways (Amanda) configure an identifier + password instead of code/alias/auto. */
    private formUsername: string = "";
    private formPassword: string = "";
    /* Gestion des contacts (FTP) : coordonnées transmises (enum backend) —
       agent (utilisateur/conseiller) / sale_point (point de vente).
       Défaut = sale_point uniquement à la création d'un portail. */
    private formCoordinatesType: string = "sale_point";

    /* Serialized form state captured when the edit modal opens, to disable Save when nothing
       changed. */
    private editSnapshot: string = "";

    private formSnapshot(): string {
        return JSON.stringify( {
            code: this.formCode,
            alias: this.formAlias,
            auto: this.formAuto,
            username: this.formUsername,
            password: this.formPassword,
            coordinatesType: this.formCoordinatesType
        } );
    }

    private get isDirty(): boolean {
        return this.formSnapshot() !== this.editSnapshot;
    }

    private get isApiGateway(): boolean {
        return this.selectedGatewayType === GATEWAY_TYPE_API;
    }

    /* Gestion des contacts options (Option = CustomValue: Id = string value, Name = label). */
    private get coordinatesTypeOptions(): Option[] {
        return [
            new CustomValue<string>(
                $t( "ADS_PUBLICATION_contactSourceNegotiator" ), "agent"
            ),
            new CustomValue<string>(
                $t( "ADS_PUBLICATION_contactSourceAgency" ), "sale_point"
            )
        ];
    }

    private get selectedCoordinatesType(): Option | undefined {
        return this.coordinatesTypeOptions.find(
            ( option: Option ) => option.Id === this.formCoordinatesType
        );
    }

    private selectCoordinatesType( option: Option ): void {
        this.formCoordinatesType = String( option.Id );
    }

    private get groupId(): number {
        return Number( this.$route.params.id );
    }

    private get customerId(): number {
        return Number( this.$route.query.customerId || 0 );
    }

    private get groupName(): string {
        return String( this.$route.query.name || "" );
    }

    /* Linked customer, carried over from the sale point list row so the portals page shows whose
       group this is (company / owner / email / address) without an extra fetch. */
    private get customerCompany(): string {
        return String( this.$route.query.company || "" );
    }

    private get customerOwner(): string {
        return String( this.$route.query.owner || "" );
    }

    private get customerEmail(): string {
        return String( this.$route.query.email || "" );
    }

    private get customerAddress(): string {
        return String( this.$route.query.address || "" );
    }

    /* Primary line of the linked customer card: prefer the fetched overview, then the row data
       carried over in the route query (shown instantly while the overview loads). */
    private get customerTitle(): string {
        if( this.overview !== null ) {
            return this.overview.Company !== ""
                ? this.overview.Company
                : this.overview.CustomerName;
        }

        return this.customerCompany !== "" ? this.customerCompany : this.customerOwner;
    }

    /* Members table shows only ACTIVE members (Enabled). Test/real groups accumulate dozens of
       disabled or never-activated seats; hiding them keeps the list to the users that actually
       count. The MEMBRES count above the table is derived from this same list so the two agree. */
    private get activeMembers(): GroupOverviewUser[] {
        if( this.overview === null )
            return [];

        return this.overview.Users.filter(
            ( member: GroupOverviewUser ): boolean => member.Enabled
        );
    }

    private get membersPager(): PagerSorter {
        return this.pagerOf( this.activeMembers.length, this.membersPage, this.membersLimit );
    }

    private get pagedMembers(): GroupOverviewUser[] {
        const pager = this.membersPager;

        return this.activeMembers.slice( pager.Offset, pager.Offset + pager.Limit );
    }

    private onMembersPage( pager: PagerSorter ): void {
        this.membersPage = pager.Page;
        this.membersLimit = pager.Limit;
    }

    private get setupsPager(): PagerSorter {
        return this.pagerOf( this.setups.length, this.setupsPage, this.setupsLimit );
    }

    private get pagedSetups(): any[] {
        const pager = this.setupsPager;

        return this.setups.slice( pager.Offset, pager.Offset + pager.Limit );
    }

    private onSetupsPage( pager: PagerSorter ): void {
        this.setupsPage = pager.Page;
        this.setupsLimit = pager.Limit;
    }

    /* A pager over a list already in memory, its page kept within the last one. */
    private pagerOf( total: number, page: number, limit: number ): PagerSorter {
        const pager = new PagerSorter( limit );

        pager.Total = total;
        pager.Page = Math.min( page, Math.max( pager.Pages, 1 ) );

        return pager;
    }

    /* Role labels of a member. An older backend sends role OBJECTS { Id, Name }: read the name,
       never « [object Object] ». */
    private roleLabels( member: GroupOverviewUser ): string {
        const labels = ( member.Roles as unknown[] ).map( ( role: unknown ): string => {
            if( typeof role === "string" )
                return role;

            if( role !== null && typeof role === "object" ) {
                const entry = role as { Name?: string; name?: string; Id?: string; id?: string };

                return entry.Name || entry.name || entry.Id || entry.id || "";
            }

            return "";
        } ).filter( ( label: string ): boolean => label !== "" );

        return labels.length === 0 ? "—" : labels.join( ", " );
    }

    /* Display a French phone number in the national grouped form (0X XX XX XX XX). Accepts the
       stored +33 / 0033 international forms (and an already-national 0…) and regroups by pairs;
       any other shape (foreign number, partial input) is shown untouched. */
    private formatPhone( phone?: string ): string {
        if( phone === undefined || phone === "" )
            return "—";

        const digits = phone.replace( /[\s.]/g, "" );

        let national: string | undefined;

        if( /^\+33\d{9}$/.test( digits ) )
            national = "0" + digits.slice( 3 );
        else if( /^0033\d{9}$/.test( digits ) )
            national = "0" + digits.slice( 4 );
        else if( /^0\d{9}$/.test( digits ) )
            national = digits;

        if( national === undefined )
            return phone;

        return national.replace( /(\d{2})(?=\d)/g, "$1 " ).trim();
    }

    /* PopupWindow primary action; Active gates submit on a resolved group user + a sent identifier
       (Identifiant envoyé is required). */
    private get addActions(): ( Identifiable & Named & Activated )[] {
        const hasUser = this.editingId !== 0 || this.selectedUserId !== 0;
        const required = this.isApiGateway
            ? this.formUsername.trim() !== ""
            : this.formCode.trim() !== "";

        return [ {
            Id: "add",
            Name: this.editingId !== 0
                ? $t( "GLOBAL_formSave" )
                : $t( "ADS_PUBLICATION_newAdsPortalsCreate" ),
            Active: hasUser && required && ( this.editingId === 0 || this.isDirty )
        } ];
    }

    private async created(): Promise<void> {
        /* Gateways load up front so edit mode can resolve a setup's gateway type (FTP vs API). */
        await Promise.all( [ this.loadOverview(), this.loadPortals(), this.loadGateways() ] );
    }

    private gatewayTypeFor( gatewayId: number ): number {
        const match = this.gateways.find( gateway => gateway.Id === gatewayId );

        return match ? match.Type : 1;
    }

    private async loadOverview(): Promise<void> {
        const [ overview, error ] = await Customers.GetGroupOverview( this.groupId );

        if( error !== undefined || overview === undefined )
            return;

        this.overview = overview;
    }

    private async loadPortals(): Promise<void> {
        this.loading = true;

        const [ setups, error ] = await AdsPublications.SetupList( this.groupId );

        this.loading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        /* Default order: auto-broadcast active portals first. */
        this.setups = setups.slice().sort(
            ( a: any, b: any ) => Number( b.Auto === true ) - Number( a.Auto === true )
        );
    }

    /* "2026-02-25 15:59:47" -> "25 févr. 2026 à 15:59" (matches the desktop list). Empty/invalid
       dates render as a dash. */
    private formatDate( value: string ): string {
        if( !value )
            return "—";

        const date = new Date( value.replace( " ", "T" ) );

        if( isNaN( date.getTime() ) )
            return "—";

        const day = date.toLocaleDateString( "fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric"
        } );
        const time = date.toLocaleTimeString( "fr-FR", {
            hour: "2-digit",
            minute: "2-digit"
        } );

        return `${ day } ${ $t( "SALE_POINT_dateAt" ) } ${ time }`;
    }

    /* Row click: open the configuration modal pre-filled to edit that setup. */
    private openEdit( setup: any ): void {
        this.editingId = Number( setup.Id );
        this.selectedGatewayId = Number( setup.GatewayId );
        this.selectedGatewayName = setup.GatewayName || "";
        this.selectedGatewayType = this.gatewayTypeFor( Number( setup.GatewayId ) );
        this.selectedUserId = Number( setup.UserId ) || 0;
        this.formCode = setup.Code || "";
        this.formAlias = setup.Name || "";
        this.formAuto = setup.Auto === true;
        this.formUsername = setup.Username || "";
        this.formPassword = "";
        /* Ancien portail non paramétré : select vide (pas de défaut forcé). */
        this.formCoordinatesType = setup.CoordinatesType || "";
        this.editSnapshot = this.formSnapshot();
        this.configuring = true;
    }

    /* Step 1: open the picker modal with the available gateways + the group users. */
    private async openPicker(): Promise<void> {
        this.resetForm();
        this.pickingPortal = true;

        await Promise.all( [ this.loadGateways(), this.loadGroupUsers() ] );
    }

    private async loadGateways(): Promise<void> {
        const [ rows, , error ] =
            await AdsPublications.Find( [], new PagerSorter( 999 ) );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.gateways = ( rows as any[] ).map( row => ( {
            Id: Number( row.Id ),
            Name: row.Name || row.name || String( row.Id ),
            Type: row.gateway_type ? Number( row.gateway_type.id ) : 1
        } ) );
    }

    private async loadGroupUsers(): Promise<void> {
        if( !this.customerId ) {
            this.groupUsers = [];
            return;
        }

        const [ users, error ] = await Customers.ListGroupUsers( this.customerId );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.groupUsers = users.map( ( user: any ) => ( {
            Id: user.Id,
            Label: user.Company ? `${ user.Name } — ${ user.Company }` : user.Name
        } ) );

        /* No user picker in the modal: the setup is created for the group's first user
           (setup-gateway/create requires a user_id). */
        this.selectedUserId = this.groupUsers.length > 0 ? this.groupUsers[ 0 ].Id : 0;
    }

    /* Step 2: a portal was picked, switch to the configuration modal. */
    private pickGateway( gateway: GatewayOption ): void {
        this.selectedGatewayId = gateway.Id;
        this.selectedGatewayName = gateway.Name;
        this.selectedGatewayType = gateway.Type;
        this.pickingPortal = false;
        this.configuring = true;
    }

    private onAutoChange( value: boolean ): void {
        this.formAuto = value;
    }

    private resetForm(): void {
        this.editingId = 0;
        this.selectedGatewayId = 0;
        this.selectedGatewayName = "";
        this.selectedGatewayType = 1;
        this.selectedUserId = 0;
        this.formCode = "";
        this.formAlias = "";
        this.formAuto = false;
        this.formUsername = "";
        this.formPassword = "";
        this.formCoordinatesType = "sale_point";
        this.editSnapshot = "";
    }

    private closePicker(): void {
        this.pickingPortal = false;
        this.resetForm();
    }

    /* Back to the portal picker from the configuration modal. */
    private backToPicker(): void {
        this.configuring = false;
        this.pickingPortal = true;
    }

    private async submitConfig(): Promise<void> {
        if( this.isApiGateway ) {
            if( this.formUsername.trim() === "" ) {
                EventBus.Publish( "Error", { Message: $t( "SALE_POINT_errorNoIdentifier" ) } );
                return;
            }
        } else if( this.formCode.trim() === "" ) {
            EventBus.Publish( "Error", { Message: $t( "ADS_PUBLICATION_actionUpdateMessage" ) } );
            return;
        }

        const error = this.editingId !== 0
            ? await this.submitEdit()
            : await this.submitNew();

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        EventBus.Publish(
            "Notification",
            CustomNotification.Success(
                $t( "ADS_PUBLICATION_createSuccessTitle" ),
                $t( "ADS_PUBLICATION_createUpdateMessage" )
            )
        );

        this.configuring = false;
        this.resetForm();
        await this.loadPortals();
    }

    private async submitNew(): Promise<any> {
        if( !this.selectedGatewayId )
            return { Message: $t( "SALE_POINT_errorNoPortal" ) };

        if( !this.selectedUserId )
            return { Message: $t( "SETUP_GATEWAY_errorNoUser" ) };

        /* API gateways (Amanda) carry an identifier + password; FTP gateways carry
           code/alias/auto. */
        const payload = this.isApiGateway
            ? {
                UserId: this.selectedUserId,
                GatewayId: this.selectedGatewayId,
                Username: this.formUsername,
                Password: this.formPassword
            }
            : {
                UserId: this.selectedUserId,
                GatewayId: this.selectedGatewayId,
                Code: this.formCode,
                Name: this.formAlias,
                Auto: this.formAuto,
                CoordinatesType: this.formCoordinatesType
            };

        const [ , error ] = await AdsPublications.SetupCreate( payload );

        return error;
    }

    private async submitEdit(): Promise<any> {
        /* The app update command requires gatewayId (it 500s otherwise: "$gatewayId must not be
           accessed before initialization"). FTP: alias is the "name" field, not "username".
           API (Amanda): identifier + password. */
        const payload = this.isApiGateway
            ? {
                GatewayId: this.selectedGatewayId,
                Username: this.formUsername,
                Password: this.formPassword
            }
            : {
                GatewayId: this.selectedGatewayId,
                Code: this.formCode,
                Name: this.formAlias,
                Auto: this.formAuto,
                CoordinatesType: this.formCoordinatesType
            };

        const [ , error ] = await AdsPublications.SetupUpdate(
            this.editingId,
            this.groupId,
            payload
        );

        return error;
    }

    /* Delete a per-user portal setup from the client's account. Guarded by a confirmation (the
       action is irreversible) and stops propagation so the row's edit click doesn't also fire. */
    private confirmDelete( setup: any, event: Event ): void {
        event.stopPropagation();

        EventBus.Publish( "Confirmation", {
            Props: {
                Message: $t(
                    "SALE_POINT_deleteConfirm",
                    { name: setup.GatewayName || setup.Name || "" }
                ),
                Actions: [ {
                    Id: "delete",
                    Name: $t( "SALE_POINT_deletePortal" ),
                    Active: true
                } ]
            },
            Events: { Action: (): Promise<void> => this.deleteSetup( setup ) }
        } );
    }

    private async deleteSetup( setup: any ): Promise<void> {
        const [ , error ] = await AdsPublications.SetupDelete(
            Number( setup.Id ),
            this.groupId
        );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        EventBus.Publish(
            "Notification",
            CustomNotification.Success(
                $t( "SALE_POINT_deleteSuccessTitle" ),
                $t( "SALE_POINT_deleteSuccessMessage" )
            )
        );

        await this.loadPortals();
    }

    private back(): void {
        this.$router.push( { name: "ads-publication-sale-point" } );
    }

    /* Open the linked customer fiche (carried via the customerId route query). */
    private openCustomer(): void {
        if( !this.customerId )
            return;

        this.$router.push( {
            name: "customer",
            params: { id: String( this.customerId ) }
        } );
    }
}
